import React from 'react';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';
import {
    AccessTokenExpiredModal,
    ErrorModal,
    FooterBox,
    IdvPendingModal,
    MaintenanceModal,
    NavBar,
    SessionExpiredModal,
    SideBar,
    UnavailableModal,
    TripSourceMultiplexer,
    PalTripSideBar,
    AllTripSideBar,
} from './components';
import { WebChat } from './components/WebChat/WebChat';
import { Routes } from './routes';
import store from './store/store';
import { IntlProvider } from 'react-intl';
import { messages } from './assets/translations/messages';
import { ToastContainer } from 'react-toastify';
import { useSegmentLoader } from './hooks';
import { UserState } from './types/user.store.type';
import { OptimizelyProvider } from '@optimizely/react-sdk';
import { nanoid } from 'nanoid';
import theme from '@vacasa/react-components-lib/lib/themes/default';
import { ThemeProvider } from '@vacasa/react-components-lib';
import { Splits, WebChatConfig } from './types/split.type';
import { ReservationCancelledModal } from './components/ReservationCancelledModal/ReservationCancelledModal';
import { TripState } from './types/trip.store.type';
import { SharedUtils } from './utils';
import { OptimizelyService } from './services/optimizely/optimizelyService';
import { useFeatureConfig } from './services/optimizely/optimizelyHelper';

import _ from 'lodash';
import { useKountLoader } from './hooks/useKountLoader';

const InternationalApp = () => {
    useSegmentLoader();

    const user: UserState = useSelector((reduxStore: { user: UserState }) => reduxStore.user);
    const trip = useSelector((store: { trip: TripState }) => store.trip);
    const language = SharedUtils.getUserLanguageCode(user);

    let { isEnabled: isWebChatEnable, config: webChatConfig } = useFeatureConfig<WebChatConfig>(Splits.GUESTS_PORTAL_WEB_CHAT, true);

    if (trip && trip.unit && isWebChatEnable && !_.isNil(webChatConfig)) {
        const unitState = `${trip.unit.country_code}-${trip.unit.state}`;
        isWebChatEnable = webChatConfig.allowedStates.includes(unitState);
    }

    return (
        <ThemeProvider theme={theme}>
            <IntlProvider locale={language} messages={messages[language] as any}>
                <div className={user.hidePage ? 'hide-page' : 'app-wrapper'}>
                    <BrowserRouter>
                        <div id="routed-app">
                            <Row>
                                <NavBar />
                            </Row>
                            <Row>
                                <Col className="px-1 no-print" sm={0} md={0} lg={3}>
                                    <AllTripSideBar />
                                </Col>
                                <Col id="main-content" xs={12} sm={12} md={12} lg={9}>
                                    <Routes />
                                </Col>
                            </Row>
                        </div>
                        <ToastContainer position="bottom-center" />
                        <AccessTokenExpiredModal />
                        <ReservationCancelledModal />
                        {trip.id && trip.unit && (
                            <WebChat
                                name={'Guest'}
                                extras={{ reservationId: trip.id, unitId: trip.unit.id }}
                                isDisabled={!isWebChatEnable}
                            />
                        )}
                    </BrowserRouter>
                    <UnavailableModal />
                    <ErrorModal />
                    <IdvPendingModal />
                    <SessionExpiredModal />
                    <MaintenanceModal />
                    <FooterBox />
                </div>
            </IntlProvider>
        </ThemeProvider>
    );
};

const optimizely = OptimizelyService.getInstance().getClient();

const App: React.FC = () => {
    useKountLoader();
    return (
        <OptimizelyProvider optimizely={optimizely} user={{ id: nanoid() }}>
            <Container>
                <Provider store={store}>
                    <InternationalApp />
                </Provider>
            </Container>
        </OptimizelyProvider>
    );
};

export default App;
